import {Product} from './Product';
import {Event} from './Event';
import {Model} from './Model';
import {Slot} from './Slot';
import {UUID4Utility} from '../Utilities';

/**
 * OrderItems, but with resolved relations for display purposes
 */
export class TicketConfiguration extends Model {
  public product: Product;
  public event: Event;
  public slot: Slot | null = null;
  public quantity: number;
  public specialProduct: boolean = false;
  public subTicketConfigurations: Array<TicketConfiguration> = [];
  public personalUse: boolean = false;
  public calculatedProductPrice: number = 0;
  public isPromoted: boolean = false;
  public initialQuantity: number = 0;
  public parentId: number | string | null = null;
  public promotion: any = null;
  public createdFrom: string | number | null = null;
  public registrationRequired: string = '';

  /**
   * The TicketConfiguration is unique through the combination of event, product and slot.
   * So if a product has 3 related slots, we have 3 ticketconfigurations for it.
   * Alternatively slotId NEEDS to be null!
   *
   * Special product determines whether a product is an advertisingproduct or is promoted
   */
  constructor(
    id: number | string | null,
    product: Product,
    event: Event,
    slot: Slot | null = null,
    specialProduct: boolean = false,
    personalUse: boolean = false,
    parentId: number | string | null = null,
    subTicketConfigurations: Array<TicketConfiguration> = [],
    promotion: any = null,
    registrationRequired: string = '',
  ) {
    super({id: id || UUID4Utility.uuid4()});
    this.product = product;
    this.event = event;
    this.quantity = 0;
    this.slot = slot;
    this.specialProduct = specialProduct;
    this.personalUse = personalUse;
    this.parentId = parentId;
    this.subTicketConfigurations = subTicketConfigurations;
    this.calculatedProductPrice = product.price;
    this.promotion = promotion;
    this.registrationRequired = registrationRequired;
  }
  resetIds() {
    this.id = UUID4Utility.uuid4();
    for (const subTicketConf of this.subTicketConfigurations) {
      subTicketConf.parentId = this.id;
      subTicketConf.id = UUID4Utility.uuid4();
    }
  }

  get isProductFamily() {
    return this.subTicketConfigurations.length > 0;
  }

  get isSubProduct() {
    return this.parentId !== null || this.product.parentProduct !== null;
  }

  get isAdditionalProduct() {
    return Boolean(this.product.additionalProduct);
  }

  get isRegistrationRequired() {
    // registrationRequired can be 'Ja', 'Nein' or 'Optional'
    return this.registrationRequired !== 'Nein';
  }

  toString(): string {
    return `${this.quantity}x ${this.product.descriptionWebShop} für Event ${this.event.descriptionExternal}`;
  }

  static clone(ticketConfigurationParam: TicketConfiguration): TicketConfiguration {
    const subconfs = ticketConfigurationParam.subTicketConfigurations.map(sc => TicketConfiguration.clone(sc));
    const slot = ticketConfigurationParam.slot === null ? null : new Slot(ticketConfigurationParam.slot);
    const config = new TicketConfiguration(
      ticketConfigurationParam.id,
      new Product(ticketConfigurationParam.product),
      new Event(ticketConfigurationParam.event),
      slot,
      ticketConfigurationParam.specialProduct,
      ticketConfigurationParam.personalUse,
      ticketConfigurationParam.parentId,
      subconfs,
      ticketConfigurationParam.promotion,
      ticketConfigurationParam.registrationRequired,
    );
    config.quantity = ticketConfigurationParam.quantity;
    config.initialQuantity = ticketConfigurationParam.initialQuantity;
    config.isPromoted = ticketConfigurationParam.isPromoted;
    config.createdFrom = ticketConfigurationParam.createdFrom;
    return config;
  }
}
